// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-control/FrameworkHealthPanel.jsx

import React from "react";
import { Button } from "../../design-system/components";
import MissionControlSection from "./MissionControlSection";
import MissionControlStatus from "./MissionControlStatus";

const PREVIEW_LIMIT = 4;

function frameworkTone(score) {
  if (!Number.isFinite(score)) return "neutral";
  if (score >= 90) return "low";
  if (score >= 75) return "medium";
  return "high";
}

function frameworkToneClass(score) {
  if (!Number.isFinite(score)) return "neutral";
  if (score >= 90) return "success";
  if (score >= 75) return "warning";
  return "danger";
}

export default function FrameworkHealthPanel({
  rows = [],
  onOpenPosture,
  onOpenFramework,
}) {
  const previewRows = rows.slice(0, PREVIEW_LIMIT);
  const remainingCount = Math.max(0, rows.length - previewRows.length);

  return (
    <MissionControlSection
      eyebrow="Framework health"
      title="Measured posture by framework"
      description="Compare operational score, active findings, accepted risk, and freshness debt."
      className="framework-health-panel"
      actions={
        <Button
          variant="secondary"
          size="small"
          onClick={onOpenPosture}
          className="framework-health-panel__open-button"
        >
          View all frameworks
        </Button>
      }
    >
      {previewRows.length === 0 ? (
        <div className="framework-health-panel__empty">
          No framework snapshots are available yet.
        </div>
      ) : (
        <>
          <div className="framework-health-panel__list">
            {previewRows.map((row) => {
              const toneClass = frameworkToneClass(row.operationalScore);
              const operationalScore = Number.isFinite(row.operationalScore) ? row.operationalScore : null;
              const rawScore = Number.isFinite(row.rawScore) ? row.rawScore : null;
              const delta = Number.isFinite(row.delta) ? row.delta : null;

              return (
                <button
                  key={row.framework}
                  type="button"
                  className={`framework-health-row framework-health-row--${toneClass}`}
                  onClick={() => onOpenFramework(row.framework)}
                  title={`Open ${row.framework} posture details`}
                >
                  <div className="framework-health-row__identity">
                    <strong>{row.framework}</strong>
                    <MissionControlStatus tone={frameworkTone(row.operationalScore)}>
                      {operationalScore === null ? "Not assessed" : `${operationalScore.toFixed(1)}%`}
                    </MissionControlStatus>
                  </div>

                  <div className="framework-health-row__bar" aria-hidden="true">
                    <span
                      style={{
                        width: `${Math.max(0, Math.min(100, operationalScore ?? 0))}%`,
                      }}
                    />
                  </div>

                  <dl className="framework-health-row__metrics">
                    <div>
                      <dt>Raw</dt>
                      <dd>{rawScore === null ? "Not assessed" : `${rawScore.toFixed(1)}%`}</dd>
                    </div>
                    <div>
                      <dt>Active</dt>
                      <dd>{row.activeFindings}</dd>
                    </div>
                    <div>
                      <dt>Accepted</dt>
                      <dd>{row.acceptedRiskFindings}</dd>
                    </div>
                    <div>
                      <dt>Stale</dt>
                      <dd>{row.staleCount}</dd>
                    </div>
                    <div>
                      <dt>Delta</dt>
                      <dd>
                        {delta === null ? "—" : `${delta > 0 ? "+" : ""}${delta.toFixed(2)}`}
                      </dd>
                    </div>
                  </dl>
                </button>
              );
            })}
          </div>

          {remainingCount > 0 ? (
            <div className="framework-health-panel__footer">
              <span>
                {remainingCount} additional framework
                {remainingCount === 1 ? "" : "s"} available
              </span>
            </div>
          ) : null}
        </>
      )}
    </MissionControlSection>
  );
}
